import { CARRY_OVER_RESTRICTED_LEGAL_ENTITIES, CARRY_OVER_UNAVAILABLE_DAYS } from '@/constants/order.constants';
import { useUserStore } from '@/stores/user';
import { FabricDisabledReason } from '@/types/matrix.enums';
import { MatrixType, type LegalEntityEnum } from '@onrunning/backstage-models';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { computed, type Ref } from 'vue';

export const useFabricValidation = (matrixType: Ref<MatrixType | undefined>) => {
  const userStore = useUserStore();
  const { accountIsDistributor } = userStore;
  const { selectedAccount: account } = storeToRefs(userStore);

  const getFabricDisabledReason = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): FabricDisabledReason | null => {
    if (!canDisableFabric.value) {
      return null;
    }

    if (isLaunchDateAfterSelectedDate(fabricLaunchDate, orderDate)) {
      return FabricDisabledReason.launchDateAfterOrderDate;
    }

    if (isCarryOverDisabled(fabricLaunchDate, orderDate, seasonStartDate)) {
      return FabricDisabledReason.carryOverRestricted;
    }

    return null;
  };

  const isLaunchDateAfterSelectedDate = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
  ): boolean => {
    if (!fabricLaunchDate) {
      return true;
    }

    const selectedOrderDate = moment(orderDate);
    const isAfterRequestedDeliveryDate = moment(fabricLaunchDate).isAfter(selectedOrderDate, 'day');

    return isAfterRequestedDeliveryDate;
  };

  const isLaunchDateBeforeSeasonStart = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): boolean => {
    if (!fabricLaunchDate) {
      return true;
    }

    const selectedOrderDate = moment(orderDate);
    const seasonStart = moment(seasonStartDate);
    const carryOverStartDate = seasonStart.clone().add(CARRY_OVER_UNAVAILABLE_DAYS, 'days');
    const isBeforeSeasonStart = moment(fabricLaunchDate).isBefore(seasonStart, 'day');

    const isSelectedDateBeforeCarryOverStartDate = selectedOrderDate.isBefore(carryOverStartDate, 'day');

    return isBeforeSeasonStart && isSelectedDateBeforeCarryOverStartDate;
  };

  const isCarryOverDisabled = (
    fabricLaunchDate: string | undefined,
    orderDate: string | Date | undefined,
    seasonStartDate: string | undefined,
  ): boolean => {
    if (!isCarryOverRestrictedLegalEntity.value) {
      return false;
    }

    return isLaunchDateBeforeSeasonStart(fabricLaunchDate, orderDate, seasonStartDate);
  };

  const canDisableFabric = computed((): boolean => {
    return matrixType.value === MatrixType.preorder && !accountIsDistributor();
  });

  const isCarryOverRestrictedLegalEntity = computed((): boolean => {
    return CARRY_OVER_RESTRICTED_LEGAL_ENTITIES.includes(account.value.legalEntity as LegalEntityEnum);
  });

  return {
    isCarryOverDisabled,
    getFabricDisabledReason,
    canDisableFabric,
    isCarryOverRestrictedLegalEntity,
  };
};
